// Safe Triangle gate for hover menus with column-wise sub-panels.
//
// Pattern (Amazon mega-menu style): once a sub-panel opens to the right of a
// trigger column, users move the cursor diagonally toward the sub-panel and
// inevitably cross sibling triggers along the way. Without a gate, each crossed
// sibling switches the open panel.
//
// This helper tracks cursor movement inside a `scope` element. Whenever the
// cursor advances toward the currently open `target` panel along a smooth
// diagonal, we mark a brief "safe" window. Callers consult that window inside
// their own mouseenter handlers and skip activation while the gate is active.
//
// Caveats:
// - Pointer devices only. Touch and keyboard skip mousemove entirely.
// - Tiny grace period (SAFE_GRACE_MS) bridges the gap between mousemove and
//   the immediately following mouseenter event on the sibling trigger.

const SAFE_GRACE_MS = 30;

function sign(p1x, p1y, p2x, p2y, p3x, p3y) {
  return (p1x - p3x) * (p2y - p3y) - (p2x - p3x) * (p1y - p3y);
}

function pointInTriangle(px, py, ax, ay, bx, by, cx, cy) {
  const d1 = sign(px, py, ax, ay, bx, by);
  const d2 = sign(px, py, bx, by, cx, cy);
  const d3 = sign(px, py, cx, cy, ax, ay);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

/**
 * @param {Object}  opts
 * @param {Element} opts.scope     - element whose mousemove we track
 * @param {Function} opts.getTarget - () => Element | null (currently open sub-panel)
 * @param {'right'|'left'} [opts.direction='right'] - expected travel direction
 * @returns {() => boolean} - call to check whether the cursor is currently
 *                            on a smooth path toward the target
 */
export function createSafeTriangleGate({ scope, getTarget, direction = 'right' }) {
  let prevX = null;
  let prevY = null;
  let safeUntil = 0;

  const onMove = (e) => {
    const target = getTarget();
    if (!target) {
      prevX = e.clientX;
      prevY = e.clientY;
      return;
    }

    if (prevX === null) {
      prevX = e.clientX;
      prevY = e.clientY;
      return;
    }

    const rect = target.getBoundingClientRect();

    // Triangle apex = previous cursor position; base = the target's edge that
    // faces the trigger column. For a right-side panel that's the LEFT edge.
    const targetEdgeX = direction === 'right' ? rect.left : rect.right;
    const movingToward = direction === 'right'
      ? e.clientX > prevX
      : e.clientX < prevX;

    if (movingToward) {
      const inside = pointInTriangle(
        e.clientX, e.clientY,
        prevX,     prevY,
        targetEdgeX, rect.top,
        targetEdgeX, rect.bottom
      );
      if (inside) {
        safeUntil = performance.now() + SAFE_GRACE_MS;
      }
    }

    prevX = e.clientX;
    prevY = e.clientY;
  };

  const onLeave = () => {
    // Reset baseline so a re-entry doesn't compute the triangle from a stale
    // cursor position elsewhere in the viewport.
    prevX = null;
    prevY = null;
  };

  scope.addEventListener('mousemove', onMove);
  scope.addEventListener('mouseleave', onLeave);

  return () => performance.now() < safeUntil;
}
